import React, { useMemo } from 'react';
import MaterialIcon from '../ui/MaterialIcon';
import BentoCard from '../ui/BentoCard';

const DEFAULT_STEPS = [
  { number: 1, title: 'Convert Points', description: 'Transfer 25,000 Chase UR to Hyatt', icon: 'sync_alt' },
  { number: 2, title: 'Apply Booking', description: 'Book 5 nights at Hyatt Regency', icon: 'hotel' },
  { number: 3, title: 'Confirm Savings', description: 'Save $847 vs. cash booking', icon: 'check_circle' },
];

const DEFAULT_BREAKDOWN = [
  { label: 'Cash Price', value: '$1,250', color: 'text-on-surface' },
  { label: 'Points Value', value: '-$847', color: 'text-success' },
  { label: 'Net Cost', value: '$403', color: 'text-primary', bold: true },
];

function PurchaseStrategiesScreen({ strategyData, onBack }) {
  const { steps, breakdown, totalSavings, secondaryStrategy, expertInsight } = useMemo(() => {
    if (!strategyData?.strategies || strategyData.strategies.length === 0) {
      return {
        steps: DEFAULT_STEPS,
        breakdown: DEFAULT_BREAKDOWN,
        totalSavings: 847,
        secondaryStrategy: 'Use Amex Platinum for airport lounge access',
        expertInsight: 'This transfer ratio offers 2.1cpp value, well above the 1.5cpp baseline',
      };
    }

    const flight = strategyData.flight;
    const price = flight ? Math.round(flight.price) : 1250;
    const topSavings = Math.round(strategyData.totalSavings || 0);
    const netCost = Math.max(0, price - topSavings);

    // Build steps from strategies
    const dynamicSteps = strategyData.strategies.slice(0, 3).map((s, i) => {
      const icons = { 'pay-with-card': 'credit_card', 'transfer-miles': 'sync_alt', 'loyalty-redeem': 'loyalty', 'travel-credit': 'card_giftcard' };
      return {
        number: i + 1,
        title: s.label,
        description: s.detail,
        icon: icons[s.type] || 'check_circle',
      };
    });

    // Build breakdown
    const dynamicBreakdown = [
      { label: 'Cash Price', value: `$${price.toLocaleString()}`, color: 'text-on-surface' },
      { label: 'Estimated Savings', value: `-$${topSavings.toLocaleString()}`, color: 'text-success' },
      { label: 'Net Cost', value: `$${netCost.toLocaleString()}`, color: 'text-primary', bold: true },
    ];

    // Secondary strategy
    let secondary = 'Use a travel credit card for airport lounge access';
    if (strategyData.travelCredits?.length > 0) {
      const credit = strategyData.travelCredits[0];
      secondary = `Apply ${credit.card} ${credit.credit}`;
    }

    // Expert insight
    let insight = '';
    if (strategyData.bestCard) {
      insight = `Earning ${strategyData.bestCard.earnRate} on travel with ${strategyData.bestCard.cardName} gives you strong value on this purchase.`;
    } else if (strategyData.transferOpportunities?.length > 0) {
      const opp = strategyData.transferOpportunities[0];
      insight = `${opp.bank} → ${opp.airline} transfers at ${opp.ratio}${opp.bonus ? ` with ${opp.bonus}` : ''} offer excellent redemption value.`;
    } else {
      insight = 'This transfer ratio offers 2.1cpp value, well above the 1.5cpp baseline';
    }

    return {
      steps: dynamicSteps.length > 0 ? dynamicSteps : DEFAULT_STEPS,
      breakdown: dynamicBreakdown,
      totalSavings: topSavings,
      secondaryStrategy: secondary,
      expertInsight: insight,
    };
  }, [strategyData]);

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-surface-container-high transition-colors"
          aria-label="Go back"
        >
          <MaterialIcon name="arrow_back" size={24} className="text-on-surface" />
        </button>
        <h1 className="text-2xl font-bold text-on-surface tracking-display">Purchase Strategy</h1>
      </div>

      {/* Strategy Timeline Card */}
      <BentoCard>
        <div className="flex items-center gap-2 mb-5">
          <MaterialIcon name="route" size={20} className="text-primary" />
          <h2 className="text-base font-semibold text-on-surface">Optimal Strategy</h2>
        </div>

        <div className="relative">
          {steps.map((step, index) => (
            <div key={step.number} className="flex gap-4 relative">
              {/* Vertical connector line */}
              {index < steps.length - 1 && (
                <div className="absolute left-5 top-10 w-0.5 h-[calc(100%-0.5rem)] bg-primary/20" />
              )}

              {/* Step number circle */}
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-on-primary">{step.number}</span>
              </div>

              {/* Step content */}
              <div className={`flex-1 ${index < steps.length - 1 ? 'pb-6' : 'pb-0'}`}>
                <div className="flex items-center gap-2">
                  <MaterialIcon name={step.icon} size={18} className="text-primary" />
                  <h3 className="text-sm font-semibold text-on-surface">{step.title}</h3>
                </div>
                <p className="text-sm text-on-surface-variant mt-1 ml-[26px]">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </BentoCard>

      {/* Savings Sidebar Bento Card */}
      <BentoCard className="bg-gradient-to-br from-success/5 to-success/10">
        {/* Total Savings */}
        <div className="mb-5">
          <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label">Total Savings</p>
          <p className="text-4xl font-bold text-success mt-1">${totalSavings.toLocaleString()}.00</p>
        </div>

        {/* Divider */}
        <div className="h-px bg-outline-variant/30 mb-5" />

        {/* Secondary Strategy */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <MaterialIcon name="credit_card" size={18} className="text-primary" />
            <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label">Secondary Strategy</p>
          </div>
          <p className="text-sm text-on-surface">{secondaryStrategy}</p>
        </div>

        {/* Expert Insight */}
        <div className="bg-white/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <MaterialIcon name="lightbulb" size={18} className="text-warning" />
            <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label">Expert Insight</p>
          </div>
          <p className="text-sm text-on-surface-variant leading-relaxed">
            {expertInsight}
          </p>
        </div>
      </BentoCard>

      {/* Comparison Breakdown Table */}
      <BentoCard>
        <div className="flex items-center gap-2 mb-4">
          <MaterialIcon name="compare_arrows" size={20} className="text-primary" />
          <h2 className="text-base font-semibold text-on-surface">Comparison Breakdown</h2>
        </div>

        <div className="space-y-0">
          {breakdown.map((row, index) => (
            <div
              key={row.label}
              className={`flex items-center justify-between py-3 ${
                index < breakdown.length - 1 ? 'border-b border-outline-variant/20' : ''
              }`}
            >
              <span className={`text-sm ${row.bold ? 'font-semibold text-on-surface' : 'text-on-surface-variant'}`}>
                {row.label}
              </span>
              <span className={`text-sm font-semibold ${row.color}`}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </BentoCard>
    </div>
  );
}

export default PurchaseStrategiesScreen;
